import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useApi } from '../contexts/ApiContext';
import {
  useGuildsCache,
  useInstancesCache,
  clearInstancesCache,
  fetchWithRetry,
} from '../hooks/useCache';
import { AgentPanel } from '../components/AgentPanel';

interface Guild {
  id: string;
  name: string;
}

interface Instance {
  guild_id: string;
  guild_name: string;
  instance_nickname: string;
  instance_id: string;
  provider: string;
  az: string;
  status: string;
  public_ip: string | null;
  allowed_channels: string[];
  registered_by_user_id: string;
  registered_by_username: string;
}

export const Instances: React.FC = () => {
  const location = useLocation();
  const { get, post } = useApi();
  const [selectedGuildId, setSelectedGuildId] = useState<string>('');
  const [guildsError, setGuildsError] = useState<string | null>(null);
  const [instancesError, setInstancesError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null); // Track which instance is being acted upon
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState<Instance | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // Use caching hooks (memoized API object so effects do not re-fire every render)
  const guildsCache = useGuildsCache();
  const instancesCache = useInstancesCache(selectedGuildId);
  const { fetchWithCache: fetchGuildsWithCache } = guildsCache;
  const { fetchWithCache: fetchInstancesWithCache } = instancesCache;

  // Fetch guilds on component mount with caching
  useEffect(() => {
    const fetchGuilds = async () => {
      try {
        await fetchGuildsWithCache(() =>
          fetchWithRetry(() => get('/guilds'), { retries: 2, baseDelayMs: 400 }),
        );
        setGuildsError(null);
      } catch (err) {
        setGuildsError(err instanceof Error ? err.message : 'Failed to load guilds');
      }
    };

    fetchGuilds();
  }, [get, fetchGuildsWithCache]);

  // Fetch instances when guild is selected with caching
  useEffect(() => {
    if (!selectedGuildId || !selectedGuildId.trim()) {
      return;
    }

    const fetchInstances = async () => {
      try {
        await fetchInstancesWithCache(() =>
          fetchWithRetry(
            () => get(`/instances?guild_id=${encodeURIComponent(selectedGuildId)}`),
            { retries: 2, baseDelayMs: 400 },
          ),
        );
        setInstancesError(null);
      } catch (err) {
        setInstancesError(err instanceof Error ? err.message : 'Failed to load instances');
      }
    };

    fetchInstances();
  }, [get, selectedGuildId, fetchInstancesWithCache]);

  const handleGuildChange = (event: SelectChangeEvent) => {
    setSelectedGuildId(event.target.value);
  };

  const handleStart = async (instance: Instance) => {
    const instanceKey = `${instance.guild_id}-${instance.instance_nickname}`;
    try {
      setActionError(null);
      setActionLoading(instanceKey);
      
      await post('/start', {
        guild_id: instance.guild_id,
        instance_nickname: instance.instance_nickname
      });
      
      // Clear cache and refresh instances after starting
      clearInstancesCache(selectedGuildId);
      await fetchInstancesWithCache(
        () => get(`/instances?guild_id=${encodeURIComponent(selectedGuildId)}`),
        { forceRefresh: true },
      );
      setInstancesError(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start instance');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStop = async (instance: Instance) => {
    const instanceKey = `${instance.guild_id}-${instance.instance_nickname}`;
    try {
      setActionError(null);
      setActionLoading(instanceKey);
      
      await post('/stop', {
        guild_id: instance.guild_id,
        instance_nickname: instance.instance_nickname
      });
      
      // Clear cache and refresh instances after stopping
      clearInstancesCache(selectedGuildId);
      await fetchInstancesWithCache(
        () => get(`/instances?guild_id=${encodeURIComponent(selectedGuildId)}`),
        { forceRefresh: true },
      );
      setInstancesError(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop instance');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (instance: Instance) => {
    setInstanceToDelete(instance);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!instanceToDelete) return;
    
    const instanceKey = `${instanceToDelete.guild_id}-${instanceToDelete.instance_nickname}`;
    try {
      setActionError(null);
      setActionLoading(instanceKey);
      setDeleteDialogOpen(false);
      
      await post('/deregister', {
        guild_id: instanceToDelete.guild_id,
        instance_nickname: instanceToDelete.instance_nickname
      });
      
      // Clear cache and refresh instances after deleting
      clearInstancesCache(selectedGuildId);
      await fetchInstancesWithCache(
        () => get(`/instances?guild_id=${encodeURIComponent(selectedGuildId)}`),
        { forceRefresh: true },
      );
      setInstancesError(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deregister instance');
    } finally {
      setActionLoading(null);
      setInstanceToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setInstanceToDelete(null);
  };

  const handleRefresh = async () => {
    if (!selectedGuildId || !selectedGuildId.trim()) return;
    
    try {
      // Clear cache and force refresh
      clearInstancesCache(selectedGuildId);
      await fetchInstancesWithCache(
        () =>
          fetchWithRetry(
            () => get(`/instances?guild_id=${encodeURIComponent(selectedGuildId)}`),
            { retries: 2, baseDelayMs: 400 },
          ),
        { forceRefresh: true },
      );
      setInstancesError(null);
    } catch (err) {
      setInstancesError(err instanceof Error ? err.message : 'Failed to refresh instances');
    }
  };

  const guilds = guildsCache.cachedData || [];
  const instances = instancesCache.cachedData || [];
  const loadingGuilds = guildsCache.isLoading && !guildsCache.cachedData;
  const loadingInstances = instancesCache.isLoading;

  if (loadingGuilds) {
    return <Typography>Loading guilds...</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Game Instances
        </Typography>

        {location.state?.message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => window.history.replaceState({}, document.title)}>
            {location.state.message}
          </Alert>
        )}

        {guildsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {guildsError}
          </Alert>
        )}
        {instancesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {instancesError}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}

        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <FormControl fullWidth>
            <InputLabel id="guild-select-label">Select Guild</InputLabel>
            <Select
              labelId="guild-select-label"
              id="guild-select"
              value={selectedGuildId}
              label="Select Guild"
              onChange={handleGuildChange}
            >
              {guilds.map((guild: Guild) => (
                <MenuItem key={guild.id} value={guild.id}>
                  {guild.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {selectedGuildId && (
            <Tooltip title="Refresh instances">
              <IconButton 
                onClick={handleRefresh}
                disabled={loadingInstances}
                color="primary"
                size="large"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {loadingInstances && (
          <Typography>Loading instances...</Typography>
        )}

        {!selectedGuildId && !loadingInstances && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Please select a guild to view its instances.
          </Alert>
        )}

        {selectedGuildId && instances.length === 0 && !loadingInstances && !instancesError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No instances registered for this guild yet. Use the Register page to add your first instance.
          </Alert>
        )}

        {instances.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Nickname</TableCell>
                  <TableCell>Instance ID</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Public IP</TableCell>
                  <TableCell>Availability Zone</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {instances.map((instance: Instance) => {
                const rowKey = `${instance.guild_id}-${instance.instance_nickname}`;
                const isExpanded = expandedKey === rowKey;
                return (
                  <React.Fragment key={rowKey}>
                  <TableRow>
                    <TableCell sx={{ width: 48 }}>
                      <IconButton
                        size="small"
                        aria-label={isExpanded ? 'Collapse agent panel' : 'Expand agent panel'}
                        onClick={() => setExpandedKey(isExpanded ? null : rowKey)}
                      >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{instance.instance_nickname}</TableCell>
                    <TableCell>{instance.instance_id}</TableCell>
                    <TableCell>{(instance.provider || 'aws').toUpperCase()}</TableCell>
                    <TableCell>{instance.status}</TableCell>
                    <TableCell>{instance.public_ip || 'N/A'}</TableCell>
                    <TableCell>{instance.az}</TableCell>
                    <TableCell>
                      {instance.status === 'stopped' && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleStart(instance)}
                          disabled={actionLoading === `${instance.guild_id}-${instance.instance_nickname}`}
                          sx={{ mr: 1 }}
                        >
                          {actionLoading === `${instance.guild_id}-${instance.instance_nickname}` ? 'Starting...' : 'Start'}
                        </Button>
                      )}
                      {instance.status === 'running' && (
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleStop(instance)}
                          disabled={actionLoading === `${instance.guild_id}-${instance.instance_nickname}`}
                        >
                          {actionLoading === `${instance.guild_id}-${instance.instance_nickname}` ? 'Stopping...' : 'Stop'}
                        </Button>
                      )}
                      {(instance.status === 'unknown' || instance.status === 'not-found') && (
                        <Button
                          variant="outlined"
                          color="warning"
                          onClick={handleRefresh}
                          disabled={loadingInstances}
                        >
                          Refresh
                        </Button>
                      )}
                      {(instance.status === 'pending' || instance.status === 'stopping') && (
                        <Button
                          variant="outlined"
                          color="info"
                          onClick={handleRefresh}
                          disabled={loadingInstances}
                        >
                          Check Status
                        </Button>
                      )}
                      
                      <Tooltip title="Deregister instance">
                        <IconButton 
                          color="error" 
                          onClick={() => handleDeleteClick(instance)}
                          disabled={actionLoading === `${instance.guild_id}-${instance.instance_nickname}`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {isExpanded && (
                          <AgentPanel
                            guildId={instance.guild_id}
                            instanceNickname={instance.instance_nickname}
                          />
                        )}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
        >
          <DialogTitle>Deregister Instance?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to deregister "{instanceToDelete?.instance_nickname}"? 
              This will remove it from Saga Bot's management, but will NOT delete the actual cloud instance.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" autoFocus>
              Deregister
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}; 